import styled from 'styled-components'

import elecciones from '@/app/2023/consejo/types/elecciones'
import TableBase from '@/app/components/Table'
import { HeaderCell } from '@/app/components/Table/Headers'
import { RowCell } from '@/app/components/Table/Row'
import { colors, spaces } from '@/config/global-parameters'

import tableColumns from './tableColumns'
import { IEstadistica, IParticipacion } from './types'

export interface IParticipationTable {
  className?: string
  circumscriptionId?: string | number
  communeId?: string
  participacion: IParticipacion
}

const filterScope = ({
  circumscriptionId,
  communeId,
  data,
}: {
  data: IParticipacion
  circumscriptionId?: string | number
  communeId?: string
}) => {
  const communeData = data?.comunas?.find((c) => String(c.comuna) === String(communeId))
  if (communeData) return communeData?.resultados
  const circumscriptionData = data?.circunscripciones?.find(
    (c) => String(c.circunscripcion) === String(circumscriptionId)
  )
  if (circumscriptionData) return circumscriptionData?.resultados
  return data?.resultados_pais || []
}

const mapDataToTableFormat = (data: IEstadistica[]) =>
  data
    .map((estadistica) => {
      const currentVotation = estadistica.eleccion === '2023_c'
      const election = elecciones[estadistica.eleccion].label
      const order = elecciones[estadistica.eleccion].order
      const participation = estadistica.c_votan
      const total = estadistica.inscritos
      const type = estadistica.tipo_votacion.toUpperCase()

      return {
        currentVotation,
        election,
        id: estadistica.eleccion,
        order,
        participation,
        total,
        type,
      }
    })
    .sort(({ order: orderA }, { order: orderB }) => {
      return orderA - orderB
    })

const ParticipationTable = ({
  circumscriptionId,
  className,
  communeId,
  participacion,
}: IParticipationTable) => {
  const filteredData = filterScope({ circumscriptionId, communeId, data: participacion }) //TODO
  if (!filteredData) return <div>Información no disponible</div>
  const tableParticipation = mapDataToTableFormat(filteredData)

  return (
    <Wrapper className={className}>
      <Table
        columns={tableColumns}
        data={tableParticipation}
      />
    </Wrapper>
  )
}

const Wrapper = styled.div`
  gap: ${spaces.s};
  width: 100%;
  height: 100%;
`

const Table = styled(TableBase)`
  width: 100%;
  @media (max-width: ${spaces.tabletBreakpoint}) {
    grid: 55px / 1.5fr 1fr 2fr 1fr;
  }
  @media (max-width: ${spaces.mobileBreakpoint}) {
    grid: 55px / 1fr 1.5fr 1.5fr 1.5fr;
  }
  ${RowCell} {
    max-height: 55px;
    padding: 0 ${spaces.xs};
    font-size: 13px;
    white-space: nowrap;
    @media (max-width: ${spaces.tabletBreakpoint}) {
      &:nth-child(n + 5):nth-child(-n + 6) {
        display: none;
      }
    }
    @media (max-width: ${spaces.tabletBreakpoint}) {
      background-color: ${colors.colorWhite};
      &:first-child {
        border-right: 0.5px solid ${colors.colorBlack};
        z-index: 1;
        position: sticky;
        top: 0;
        left: 0;
        box-shadow: 0px 19px 20px 0px rgba(0, 0, 0, 0.3);
      }
    }
  }
  ${HeaderCell} {
    justify-content: center;
    max-height: 55px;
    &:first-child {
      font-weight: 700;
      justify-content: start;
    }
    @media (max-width: ${spaces.tabletBreakpoint}) {
      &:nth-child(n + 5):nth-child(-n + 6) {
        display: none;
      }
    }
    @media (max-width: ${spaces.tabletBreakpoint}) {
      background-color: ${colors.colorWhite};
      &:first-child {
        border-right: 0.5px solid ${colors.colorBlack};
        padding: ${spaces.xs};
        z-index: 1;
        position: sticky;
        top: 0;
        left: 0;
        box-shadow: 0px 19px 20px 0px rgba(0, 0, 0, 0.3);
      }
    }
  }
  > :nth-child(2) {
    > * {
      background: ${colors.colorGrey10};
    }
  }
  > :not(:nth-child(2)) {
    &:hover {
      > * {
        background-color: ${colors.colorWhite};
      }
    }
  }
`

export default ParticipationTable
